import { buyWithTon, waitForPurchase } from "../utils/purchaseHelpers";
import WebApp from "@twa-dev/sdk";
import { safeOpenTelegramLink } from "../utils/linkHelpers";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import Loading from "../components/Loading";
import { FaPlayCircle, FaUserFriends, FaSearch, FaTimes, FaGem, FaTelegram, FaFutbol } from "react-icons/fa";
import { Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTitle } from "../hooks/useTitle";
import ReactGA from "react-ga4";
import { toast } from "react-toastify";
import confetti from "canvas-confetti";
import AdSense from "react-adsense";
import { useUser } from "../context/UserContext";
import { TonConnectButton, useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import DailyCheckIn from "../components/DailyCheckIn";
import {
  requestInvoiceLink,
} from "../utils/paymentHelpers";
import { updateUserLinks } from "../utils/inviteHelpers";
import Joyride from "react-joyride";
import { homeSteps } from "../data/taskSteps";
import CustomTooltip from "../components/CustomTooltips";
import useOnboarding from "../hooks/useOnBoarding";
import Tabs from "../components/Tabs";
import ChannelCard from "../components/ChannelCard";
import ChannelDetailSheet from "../components/ChannelDetailSheet";
import haptic from "../utils/haptic";
import EmptyState from "../components/EmptyState";
import { ChannelGridSkeleton } from "../components/ChannelCardSkeleton";

export default function Home() {
  useTitle("Home - Biscope Store");
  const navigate = useNavigate();
  const { userData, setUserData, loading, APIKey, setHeaderHidden } = useUser();
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const [tonReceiverAddress, setTonReceiverAddress] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("channels");
  const [swipeDirection, setSwipeDirection] = useState(1);
  const [buttonLoading, setButtonLoading] = useState(false);
  // Sheet state
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [sheetPurchaseState, setSheetPurchaseState] = useState("idle"); // "idle"|"loading"|"success"|"error"
  const [generatedLink, setGeneratedLink] = useState(null);
  const selectedChannelRef = useRef(null); // mirror for use inside event callbacks
  // Persist channel filter across tab switches
  const [channelFilter, setChannelFilter] = useState(
    () => sessionStorage.getItem("channelFilter") || "free"
  );
  const [searchQuery, setSearchQuery] = useState("");
  // TON wallet banner dismissal (persisted for the session)
  const [walletDismissed, setWalletDismissed] = useState(
    () => localStorage.getItem("tonBannerDismissed") === "true"
  );

  const handleDismissWallet = () => {
    localStorage.setItem("tonBannerDismissed", "true");
    setWalletDismissed(true);
  };

  const handleChannelFilterChange = (val) => {
    sessionStorage.setItem("channelFilter", val);
    setChannelFilter(val);
  };

  const handleTabChange = (newTab) => {
    const tabs = ["channels", "invites"];
    const currentIndex = tabs.indexOf(activeTab);
    const newIndex = tabs.indexOf(newTab);
    setSwipeDirection(newIndex > currentIndex ? 1 : -1);
    setActiveTab(newTab);
  };
  const channelsRef = useRef(null);
  const pendingPaymentRef = useRef({ channelId: null, orderId: null });
  const userIdRef = useRef(null);
  const sentinelRef = useRef(null);
  const { run, handleCallback, restart } = useOnboarding("hasSeenHomeTour");

  // Hide header when the sticky filter bar scrolls past the top of the viewport.
  // Using a scroll listener instead of IntersectionObserver because Telegram
  // Mini App webviews scroll via window, not always the IO viewport root.
  useEffect(() => {
    const handleScroll = () => {
      if (!sentinelRef.current) return;
      const rect = sentinelRef.current.getBoundingClientRect();
      setHeaderHidden(rect.top <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      setHeaderHidden(false);
    };
  }, [setHeaderHidden]);

  const handleQuickChannels = () => {
    haptic.tap();
    setActiveTab("channels");
    setTimeout(() => channelsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const handleQuickInvites = () => {
    haptic.tap();
    setActiveTab("invites");
    setTimeout(() => channelsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // ── Sheet open / close ──────────────────────────────────────────────────────
  const openChannelSheet = (channelId) => {
    const channel = (userData.channels || []).find((ch) => ch._id === channelId);
    if (!channel) return;
    setSelectedChannel(channel);
    selectedChannelRef.current = channel;
    setSheetPurchaseState("idle");
    setGeneratedLink(null);
  };

  const closeChannelSheet = () => {
    setSelectedChannel(null);
    selectedChannelRef.current = null;
    setSheetPurchaseState("idle");
    setGeneratedLink(null);
  };

  // ── Telegram BackButton closes the sheet ────────────────────────────────────
  useEffect(() => {
    if (selectedChannel) {
      WebApp.BackButton.show();
      const handler = () => closeChannelSheet();
      WebApp.BackButton.onClick(handler);
      return () => WebApp.BackButton.offClick(handler);
    } else {
      WebApp.BackButton.hide();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel]);

  // ── Deep-link: auto-open sheet when channels are ready ─────────────────────
  useEffect(() => {
    const deepLinkId = sessionStorage.getItem("deepLinkChannelId");
    if (!deepLinkId || !userData.channels) return;
    const target = userData.channels.find((ch) => ch._id === deepLinkId);
    if (!target) return;
    sessionStorage.removeItem("deepLinkChannelId");
    setSelectedChannel(target);
    selectedChannelRef.current = target;
    setSheetPurchaseState("idle");
    setGeneratedLink(null);
  }, [userData.channels]);

  // ── Deep-link: route redirection ─────────────────────────────────────────────
  useEffect(() => {
    const deepLinkRoute = sessionStorage.getItem("deepLinkRoute");
    if (deepLinkRoute) {
      sessionStorage.removeItem("deepLinkRoute");
      navigate(deepLinkRoute);
    }
  }, [navigate]);

  // Fetch TON receiver address on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/store/ton-config`, { headers: { "x-api-key": APIKey } })
      .then(res => setTonReceiverAddress(res.data.tonAddress))
      .catch(() => {});
  }, []);

  // Fetch channels data and sort them
  const fetchChannelsData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/channels`, {
        params: { initData: WebApp.initData },
        headers: { "x-api-key": APIKey },
      });
      const sortedChannels = response.data.sort((a, b) => {
        const aIsSuper = a.isSuperFeatured ?? false;
        const bIsSuper = b.isSuperFeatured ?? false;
        
        if (aIsSuper !== bIsSuper) {
          return bIsSuper - aIsSuper;
        }

        const aIsFeatured = a.isFeatured ?? false;
        const bIsFeatured = b.isFeatured ?? false;

        if (aIsFeatured !== bIsFeatured) {
          return bIsFeatured - aIsFeatured;
        }

        // Sort by creation date (recent first)
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        if (dateA !== dateB) {
          return dateB - dateA;
        }

        // Sort by status
        const statusOrder = ["inactive", "active", "closed", "archived"];
        return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
      });
      setUserData((prev) => ({ ...prev, channels: sortedChannels }));
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  // Filter links created within the last 2 hours
  const filterRecentLinks = (link) => {
    const linkCreatedAt = Date.parse(link.createdAt);
    const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
    return linkCreatedAt >= twoHoursAgo;
  };

  // Update userIdRef whenever userData changes
  useEffect(() => {
    userIdRef.current = userData.userId || userData.user?.userId;
  }, [userData]);

  // Fetch channels when component mounts
  useEffect(() => {
    if (userData.userId) {
      fetchChannelsData();
    }
  }, [userData.userId]);

  // (Get More Gems flow is now handled inline inside ChannelDetailSheet)

  // ── Stars invoice result handler ────────────────────────────────────────────
  useEffect(() => {
    const handleInvoiceClosed = async (event) => {
      const { status } = event;
      const channelId = pendingPaymentRef.current.channelId;
      const userId = userIdRef.current;
      if (!channelId || !pendingPaymentRef.current.orderId) return;

      if (status === "paid") {
        setPaymentLoading(true);
        setButtonLoading(true);
        try {
          const orderId = pendingPaymentRef.current.orderId;
          const order = await waitForPurchase(orderId, APIKey);
          if (order.state !== "fulfilled") throw new Error(`Payment is processing. Resume in Profile → Purchases. Order: ${orderId}`);
          const inviteLink = order.inviteLink;
          if (inviteLink) {
            haptic.success();
            await updateUserLinks(inviteLink, setUserData, userId, APIKey);
            confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            ReactGA.event({ category: "Purchase", action: "Purchase successful", label: "Stars" });

            // If sheet is still showing the paid channel → show success state
            if (selectedChannelRef.current?._id === channelId) {
              setGeneratedLink(inviteLink);
              setSheetPurchaseState("success");
            } else {
              // Sheet was closed — fall back to the invite links tab
              toast.success("🎉 Payment successful! Check your Invite Links tab.", {
                position: "bottom-center",
                autoClose: 3000,
              });
            }
          }
        } catch (error) {
          console.error("Error generating invite link after Stars payment:", error);
          if (selectedChannelRef.current?._id === channelId) {
            setSheetPurchaseState("error");
          } else {
            WebApp.showPopup(
              {
                title: "Error",
                message: "Payment was received but invite link generation failed. Please contact support.",
                buttons: [{ id: "support", type: "default", text: "Support" }],
              },
              (buttonId) => {
              if (buttonId === "support") safeOpenTelegramLink("https://t.me/biscopesupport_bot");
              }
            );
          }
        } finally {
          setPaymentLoading(false);
          setButtonLoading(false);
        }
      } else if (status === "cancelled") {
        setButtonLoading(false);
      } else {
        setButtonLoading(false);
        WebApp.showPopup({
          title: `Payment ${status}`,
          message: `Status: ${status}`,
          buttons: [{ type: "destructive", text: "Close" }],
        });
      }
    };
    WebApp.onEvent("invoiceClosed", handleInvoiceClosed);
    return () => { WebApp.offEvent("invoiceClosed", handleInvoiceClosed); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stars purchase: open Telegram invoice (result via invoiceClosed event) ──
  const handleStarsSheetPurchase = async (channel) => {
    haptic.tap();
    ReactGA.event({ category: "Purchase", action: "Channel purchased", label: "Channel - " + channel._id });
    try {
      setButtonLoading(true);
      const { invoiceLink, orderId } = await requestInvoiceLink(userData.userId, channel, APIKey);
      pendingPaymentRef.current = { channelId: channel._id, orderId };
      WebApp.openInvoice(invoiceLink);
    } catch (error) {
      setButtonLoading(false);
      setSheetPurchaseState("error");
      toast.error(
        error?.response?.data?.error
          ? `Payment failed: ${error.response.data.error}`
          : error?.message ? `Payment failed: ${error.message}` : "Payment failed",
        { position: "bottom-center", autoClose: 2000 }
      );
    }
  };

  // ── TON purchase ────────────────────────────────────────────────────────────
  const handleTonSheetPurchase = async (channel) => {
    haptic.tap();
    if (!tonConnectUI.connected) {
      toast.info("Connect your TON wallet in the Profile tab first!");
      tonConnectUI.openModal();
      return;
    }
    if (!tonReceiverAddress) {
      toast.error("TON receiver address not configured on the server.");
      return;
    }
    setButtonLoading(true);
    setSheetPurchaseState("loading");
    try {
      const result = await buyWithTon({ channelId: channel._id }, userData.userId, APIKey, tonConnectUI);
      const res = { data: { ...result, success: result.state === "fulfilled" } };
      if (!res.data.success) {
        toast.info(`Purchase pending. Resume in Profile → Purchases. Order: ${result.orderId}. Do not pay again.`);
        setSheetPurchaseState("idle");
        return;
      }

      if (res.data.success) {
        haptic.success();
        await updateUserLinks(res.data.inviteLink, setUserData, userData.userId, APIKey, channel.name);
        setGeneratedLink(res.data.inviteLink);
        setSheetPurchaseState("success");
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      } else {
        setSheetPurchaseState("error");
        toast.error("Blockchain verification failed. Contact support.");
      }
    } catch (err) {
      console.error("TON channel purchase error:", err);
      setSheetPurchaseState("error");
      if (err?.message?.includes("Reject")) {
        toast.info("Transaction cancelled.");
      } else {
        haptic.error();
        toast.error(err?.response?.data?.error || "Unable to complete purchase. Please check the requirements and try again.");
      }
    } finally {
      setButtonLoading(false);
    }
  };

  // ── Gems purchase ───────────────────────────────────────────────────────────
  const handleGemsSheetPurchase = async (channel) => {
    haptic.tap();
    ReactGA.event({ category: "Invite", action: "Invite link generated", label: "Channel - " + channel._id });
    setSheetPurchaseState("loading");
    setButtonLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/channel/${channel._id}/createInviteLink/${userData.userId}`,
        { params: { initData: WebApp.initData }, headers: { "x-api-key": APIKey } }
      );
      if (response.data.inviteLink) {
        const inviteLink = response.data.inviteLink;
        // Optimistic local update
        setUserData((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            points: prev.user.points - channel.points,
            links: [...(prev.user.links || []), {
              name: channel.name,
              link: inviteLink,
              channelName: channel.name,
              createdAt: new Date().toISOString()
            }],
          },
        }));
        // Server reconcile
        axios.get(`${API_URL}/api/user/${userData.userId}`, {
          params: { initData: WebApp.initData },
          headers: { "x-api-key": APIKey },
        }).then((r) => {
          if (r.data) setUserData((prev) => ({ ...prev, user: r.data }));
        }).catch(() => {});

        setGeneratedLink(inviteLink);
        setSheetPurchaseState("success");
        haptic.success();
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      }
    } catch (error) {
      console.error("Error generating invite link:", error);
      setSheetPurchaseState("error");
      haptic.error();
    } finally {
      setButtonLoading(false);
    }
  };

  // (Gems/Stars/TON purchase handlers above replace the old modal-based flow)

  if (loading) return <Loading />;

  if (userData.user?.isBanned) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="bg-slate-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-black text-slate-900 dark:text-white min-h-screen pb-24 p-4 transition-colors duration-300"
      >
        <h1 className="text-4xl font-bold text-red-600">Access Denied</h1>
        <p className="text-xl mt-4">You are banned from using this app.</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-6"
          onClick={() => {
            haptic.tap();
            safeOpenTelegramLink("https://t.me/biscopesupport_bot");
          }}
        >
          Contact Support
        </motion.button>
      </motion.div>
    );
  }
  if (!userData.user) return <Loading />;

  const recentLinks = (userData.user?.links?.filter(filterRecentLinks) || []).sort((a, b) => {
    const isActiveA = new Date(a.createdAt).getTime() + 3600000 > Date.now();
    const isActiveB = new Date(b.createdAt).getTime() + 3600000 > Date.now();
    if (isActiveA && !isActiveB) return -1;
    if (!isActiveA && isActiveB) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  // Filter channels: free | gems | paid
  const filteredChannels = userData?.channels?.filter(channel => {
    const matchesFilter = (() => {
      if (channel.isSuperFeatured) return true;

      if (channelFilter === "free") {
        // Completely free — no cost in any currency
        return (
          (!channel.points || channel.points === 0) &&
          (!channel.price  || channel.price  === 0) &&
          (!channel.stars  || channel.stars  === 0) &&
          (!channel.tonStars || channel.tonStars === 0)
        );
      }
      if (channelFilter === "gems") {
        // Purchasable with gems (may also have other prices)
        return channel.points > 0;
      }
      if (channelFilter === "paid") {
        // Requires kyats, Stars, or TON
        return channel.price > 0 || channel.stars > 0 || channel.tonStars > 0;
      }
      return true;
    })();

    // Search filter — case-insensitive match on channel name
    const matchesSearch = searchQuery.trim() === ""
      || channel.name?.toLowerCase().includes(searchQuery.trim().toLowerCase());

    return matchesFilter && matchesSearch;
  }) || [];

  // Badge counts only links still joinable (< 1 hour old)
  const activeLinksCount = recentLinks.filter(
    (l) => new Date(l.createdAt).getTime() + 3_600_000 > Date.now()
  ).length;

  const homeTabs = [
    {
      id: "channels",
      label: "Channels",
      badge: filteredChannels.length || 0,
    },
    {
      id: "invites",
      label: "Invite Links",
      badge: activeLinksCount || 0,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white min-h-screen pb-24 transition-colors duration-300"
    >

      <Joyride
        steps={homeSteps}
        run={run}
        callback={handleCallback}
        tooltipComponent={CustomTooltip}
        showSkipButton
        showProgress
        continuous
        scrollToFirstStep
        scrollOffset={50}
        disableOverlayClose
        spotlightClicks={false}
        styles={{
          options: { zIndex: 10000, arrowColor: "#475569" },
          spotlight: {
            borderRadius: "12px",
            boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.6)",
          },
        }}
      />

      {/* Payment processing overlay (Stars only — while waiting for invoice) */}
      {paymentLoading && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-xl">
            <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-800 dark:text-white text-center">Payment processing...</p>
          </div>
        </div>
      )}

      {/* Channel Detail Sheet */}
      <AnimatePresence>
        {selectedChannel && (
          <ChannelDetailSheet
            channel={selectedChannel}
            onClose={closeChannelSheet}
            userId={userData.userId || userData.user?.userId}
            userPoints={userData.user?.points || 0}
            existingLinks={userData.user?.links || []}
            onGemsPurchase={handleGemsSheetPurchase}
            onStarsPurchase={handleStarsSheetPurchase}
            onTonPurchase={handleTonSheetPurchase}
            purchaseState={sheetPurchaseState}
            generatedLink={generatedLink}
            buttonLoading={buttonLoading}
          />
        )}
      </AnimatePresence>
      <div className="container mx-auto px-4 pt-20 pb-2">
        {/* Dismissable TON Wallet Banner */}
        {!wallet && !walletDismissed && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 bg-slate-800/70 backdrop-blur border border-slate-600/40 rounded-2xl px-4 py-3 shadow-md flex items-center justify-between gap-3"
          >
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <span className="text-amber-400">🔗</span> Connect your TON Wallet
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <div className="z-10"><TonConnectButton /></div>
              <button
                onClick={handleDismissWallet}
                className="text-white/40 hover:text-white/80 text-xs transition-colors px-1"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}

        {/* Daily Check-In — primary engagement, shown first */}
        <DailyCheckIn userData={userData} setUserData={setUserData} APIKey={APIKey} />

        {/* Football Promo Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          onClick={() => { haptic.tap(); navigate("/game/football"); }}
          className="mb-4 relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-green-500 p-4 shadow-lg shadow-emerald-500/20 cursor-pointer active:scale-95 transition-transform"
        >
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 -mr-4 -mt-4 opacity-20 text-7xl text-white"><FaFutbol /></div>
          <div className="absolute bottom-0 left-0 -ml-4 -mb-4 opacity-10 text-6xl text-white"><FaFutbol /></div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex-1 pr-4">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest text-white uppercase mb-2 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse shadow-sm shadow-red-500/50"></span>
                New Game
              </div>
              <h3 className="text-white font-bold text-sm sm:text-base leading-snug drop-shadow-sm">
                ဘောလုံးပွဲရလဒ်ခန့်မှန်းပြီး <br className="hidden sm:block" /> Gem တွေ ဆပွားကြမယ်
              </h3>
            </div>
            <div className="w-10 h-10 shrink-0 bg-white rounded-full flex items-center justify-center shadow-lg shadow-black/10">
              <FaFutbol className="text-emerald-500 text-xl drop-shadow-sm" />
            </div>
          </div>
        </motion.div>

        {/* Quick Links Row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="grid grid-cols-4 gap-3 mb-4"
        >
          {[
            {
              emoji: "⛏️",
              label: "Miner",
              onClick: () => { haptic.tap(); navigate("/miner"); },
              gradient: "from-amber-400 to-orange-500",
              shadow: "shadow-amber-500/30",
              badge: "NEW",
            },
            {
              emoji: "🎬",
              label: "Channels",
              onClick: handleQuickChannels,
              badge: userData.channels?.length > 0 ? userData.channels.length : null,
              gradient: "from-purple-500 to-fuchsia-500",
              shadow: "shadow-purple-500/30",
            },
            {
              emoji: "🍿",
              label: "Library",
              onClick: () => { haptic.tap(); navigate("/purchased-movies"); },
              gradient: "from-rose-400 to-pink-500",
              shadow: "shadow-rose-500/30",
            },
            {
              emoji: "👥",
              label: "Invite",
              onClick: () => { haptic.tap(); navigate("/profile", { state: { scrollTo: "referral" } }); },
              gradient: "from-sky-400 to-cyan-500",
              shadow: "shadow-sky-500/30",
            },
          ].map((item) => (
            <motion.button
              key={item.label}
              whileHover={{ scale: 1.06, y: -2 }}
              whileTap={{ scale: 0.94 }}
              onClick={item.onClick}
              className={`relative flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-2xl bg-gradient-to-br ${item.gradient} ${item.shadow} shadow-md text-white`}
            >
              {item.badge != null && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-sm animate-pulse">
                  {item.badge}
                </span>
              )}
              <span className="text-2xl leading-none">{item.emoji}</span>
              <span className="text-[10px] font-bold tracking-wide uppercase opacity-90">{item.label}</span>
            </motion.button>
          ))}
        </motion.div>

        <button
          onClick={restart}
          className="restartTour fixed bottom-20 right-6 z-50 text-fuchsia-600 transition-all flex items-center"
          title="Restart Tour"
        >
          <FaPlayCircle className="inline-block mr-1 text-2xl" />
        </button>

        <div className="mt-4 px-4 mx-auto overflow-hidden relative z-0">
          <AdSense.Google
            client="ca-pub-4252052358641832"
            slot="4385377268"
            style={{ display: "block" }}
            format="auto"
            responsive="true"
            layoutKey="-gw-1+2a-9x+5c"
          />
        </div>
      </div>
      <div ref={channelsRef} />
      
      {/* Main Sticky Header */}
      <div className="sticky top-0 z-40 bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur-md pt-2 pb-2">
        <Tabs activeTab={activeTab} setActiveTab={handleTabChange} tabs={homeTabs} />
        
        <AnimatePresence>
          {activeTab === "channels" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-4 pt-3 pb-1 space-y-2 border-b border-slate-200/60 dark:border-slate-700/60">
                {/* Search bar */}
                <div className="relative w-full">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={13} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search channels..."
                    className="w-full pl-9 pr-9 py-2 text-sm rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-400 dark:focus:ring-purple-500 transition"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition"
                      aria-label="Clear search"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>

                {/* Channel Filter Toggle */}
                <div className="flex items-center bg-slate-200/50 dark:bg-slate-700/50 p-1 rounded-xl w-full">
                  {[
                    { id: "free",  label: "Free"  },
                    { id: "gems",  label: "Gems"  },
                    { id: "paid",  label: "Paid"  },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => handleChannelFilterChange(f.id)}
                      className={`filter-${f.id} flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                        channelFilter === f.id
                          ? "bg-white dark:bg-slate-800 text-[#9333EA] shadow-sm"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Swipeable Tab Content */}
      <div
        className="mt-4 overflow-hidden"
        onTouchStart={(e) => { e._swipeStartX = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const delta = e.changedTouches[0].clientX - (e._swipeStartX || e.changedTouches[0].clientX);
          if (delta < -60) handleTabChange("invites");
          else if (delta > 60) handleTabChange("channels");
        }}
      >
        <motion.div
          className="flex"
          animate={{ x: activeTab === "channels" ? "0%" : "-50%" }}
          transition={{ type: "spring", stiffness: 350, damping: 35 }}
          style={{ width: "200%" }}
        >
          {/* Channels panel — always mounted */}
          <div className="w-1/2 px-4">
            <motion.div
              variants={containerVariants}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-full max-w-7xl mx-auto px-4 py-8"
            >
        {userData.channels === null ? (
          <ChannelGridSkeleton count={3} />
        ) : userData.channels.length > 0 ? (
          <>
            {/* Section heading */}
            <div className="flex items-center gap-2 px-4 mt-6 mb-4">
              <span className="text-lg">🎬</span>
              <h2 className="text-base font-semibold text-slate-700 dark:text-slate-200">Available Channels</h2>
              <span className="ml-2 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2 py-0.5 rounded-full">{filteredChannels.length}</span>
            </div>

            {/* Sentinel — triggers header hide when scrolled past */}
            <div ref={sentinelRef} className="h-px mb-4" />

            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredChannels.length > 0 ? (
                  filteredChannels.flatMap((channel, index) => {
                    const card = (
                      <ChannelCard
                        key={channel.chatId}
                        channel={channel}
                        onClick={() => { haptic.tap(); openChannelSheet(channel._id); }}
                      />
                    );
                    // Insert an ad banner after every 5th card
                    if ((index + 1) % 5 === 0 && index + 1 < filteredChannels.length) {
                      return [
                        card,
                        <div key={`ad-${index}`} className="col-span-full my-4 flex flex-col items-center">
                          <div className="text-black dark:text-white text-xs mb-3 flex items-center gap-1 animate-bounce">
                            <span>Scroll down for more channels</span>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                            </svg>
                          </div>
                          <AdSense.Google
                            client="ca-pub-4252052358641832"
                            slot="4385377268"
                            style={{ display: "block", width: "100%" }}
                            format="auto"
                            responsive="true"
                            layoutKey="-gw-1+2a-9x+5c"
                          />
                        </div>
                      ];
                    }
                    return [card];
                  })
                ) : (
                  <div className="col-span-full py-10 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 gap-2">
                    <FaSearch size={20} className="opacity-40" />
                    <p className="text-sm">
                      {searchQuery
                        ? `No channels found for "${searchQuery}"`
                        : `No ${channelFilter} channels found.`}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-xs text-purple-500 hover:text-purple-700 underline mt-1"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </motion.div>

            {/* Referral Banner — shown after channel content, not before */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="rounded-2xl overflow-hidden mt-6 mb-2 relative"
            >
              <div className="bg-gradient-to-r from-[#0C8BC4] via-sky-500 to-cyan-600 dark:from-sky-800 dark:via-[#0C8BC4] dark:to-cyan-900 p-5">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl pointer-events-none" />
                <div className="absolute bottom-0 left-8 w-20 h-20 bg-sky-300/20 rounded-full translate-y-1/2 blur-xl pointer-events-none" />
                <div className="relative flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FaUserFriends className="text-white/80" size={16} />
                      <span className="text-white/80 text-xs font-medium uppercase tracking-wide">Referral Program</span>
                    </div>
                    <h2 className="text-white font-bold text-lg leading-tight">Invite Friends,<br />Earn Gems!</h2>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-amber-300 font-bold text-sm">+100</span>
                      <FaGem className="text-amber-300" size={12} />
                      <span className="text-white/70 text-xs">per referral</span>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => { haptic.tap(); navigate("/profile"); }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="shrink-0 bg-white text-[#0C8BC4] font-bold text-sm py-2.5 px-5 rounded-xl shadow-lg hover:bg-amber-50 transition-colors"
                  >
                    Invite Now
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          <EmptyState
            icon="channels"
            title="No channels yet"
            subtitle="New channels are coming soon! Check back later."
          />
        )}
          </motion.div>
        </div>

          {/* Invite Links panel — always mounted */}
          <div className="w-1/2 px-4">
            <motion.div
              key="invites"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-bold text-slate-800 dark:text-white">Recent Invite Links</h2>
              {recentLinks.filter(l => new Date(l.createdAt).getTime() + 3600000 > Date.now()).length > 0 && (
                <span className="bg-[#0C8BC4]/10 dark:bg-[#0C8BC4]/20 text-[#0C8BC4] text-xs font-semibold px-2 py-0.5 rounded-full">
                  {recentLinks.filter(l => new Date(l.createdAt).getTime() + 3600000 > Date.now()).length} active
                </span>
              )}
            </div>
            {recentLinks.length > 0 ? (
              <motion.div
                className="grid grid-cols-1 gap-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {recentLinks.map((link, index) => {
                  const linkCreatedAt = new Date(link.createdAt);
                  const expirationTime = new Date(
                    linkCreatedAt.getTime() + 60 * 60 * 1000
                  );
                  const timeRemaining = expirationTime - Date.now();
                  const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
                  const minutes = Math.floor(
                    (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
                  );
                  const seconds = Math.floor(
                    (timeRemaining % (1000 * 60)) / 1000
                  );
                  const isExpired = expirationTime <= Date.now();

                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      whileHover={{ scale: 1.01 }}
                      className={`rounded-2xl overflow-hidden shadow-md border-2 transition-colors ${
                        isExpired
                          ? "border-red-200 dark:border-red-500/20 bg-white dark:bg-slate-800/60"
                          : "border-[#0C8BC4]/30 dark:border-[#0C8BC4]/20 bg-white dark:bg-slate-800"
                      }`}
                    >
                      {/* Card header */}
                      <div className={`px-4 pt-4 pb-2 ${
                        isExpired ? "" : "border-b border-slate-100 dark:border-slate-700/50"
                      }`}>
                        <div className="flex justify-between items-start">
                          <h3 className="font-semibold text-slate-900 dark:text-white text-base">{link.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            isExpired
                              ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                              : "bg-[#0C8BC4]/10 dark:bg-[#0C8BC4]/20 text-[#0C8BC4]"
                          }`}>
                            {isExpired ? "Expired" : "Active"}
                          </span>
                        </div>
                        {/* Truncated link preview */}
                        <p className="text-slate-400 dark:text-slate-500 text-xs mt-1 truncate">{link.link}</p>
                      </div>

                      {/* Expiry countdown or expired message */}
                      <div className="px-4 py-3">
                        {!isExpired ? (
                          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/20 rounded-xl px-3 py-2">
                            <Clock className="h-4 w-4 text-amber-500 dark:text-amber-400 shrink-0" />
                            <div className="text-sm">
                              <span className="text-slate-500 dark:text-slate-400">Expires in </span>
                              <span className="font-bold text-amber-600 dark:text-amber-300 tabular-nums">
                                {hours}h {minutes}m {seconds}s
                              </span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-slate-400 dark:text-slate-500 text-sm text-center">This link has expired.</p>
                        )}
                      </div>

                      {/* Join button */}
                      <div className="px-4 pb-4">
                        <motion.button
                          whileHover={!isExpired ? { scale: 1.02 } : {}}
                          whileTap={!isExpired ? { scale: 0.98 } : {}}
                          disabled={isExpired}
                          onClick={() => !isExpired && safeOpenTelegramLink(link.link)}
                          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                            isExpired
                              ? "bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                              : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md shadow-blue-500/20"
                          }`}
                        >
                          <FaTelegram size={16} />
                          {isExpired ? "Link Expired" : "Join Channel"}
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <EmptyState
                icon="links"
                title="No invite links yet"
                subtitle="Get an invite link from the Channels tab to start sharing."
              />
            )}
          </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

